import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
// import { apiClient } from '../lib/apiClient';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Loader2, AlertCircle, UserCircle, Mail, Phone, Camera } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { updateUserProfile } from '../services/authService';
import toast from 'react-hot-toast';
// import apiClient from '../lib/apiClient'; // Removed apiClient

export default function UserProfilePage() {
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState(user?.name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Profile picture state (for UI only in this step)
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(user?.profileImageUrl || null); // Assuming user object might have profileImageUrl

  useEffect(() => {
    if (user?.name) {
      setFullName(user.name);
    }
    if (user?.profileImageUrl) { // Check if profileImageUrl exists on user object
        setImagePreview(user.profileImageUrl);
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) {
    // Should be handled by ProtectedRoute or AuthContext, but as a fallback:
    navigate('/auth/sign-in'); // Or your app's login route
    return null;
  }

  const handleNameUpdateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError(t('fullNameCannotBeEmpty'));
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      // Call the new updateUserProfile function
      await updateUserProfile({ name: fullName.trim() });
      await queryClient.invalidateQueries({ queryKey: ['userSession'] });
      setSuccessMessage(t('nameUpdatedSuccessfully'));
      setIsEditingName(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('failedToUpdateName'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
      setSuccessMessage(null); // Clear previous success message
      setError(null); // Clear previous error
    }
  };

  const handleImageUpload = async () => {
    if (!profileImage) return;
    setSuccessMessage(null);
    setError(null);
    // This is where backend integration for image upload would go.
    // For now, it's a placeholder.
    toast.success(t('profileImageUploadNotImplemented'));
    // Example:
    // const formData = new FormData();
    // formData.append('profileImage', profileImage);
    // try {
    //   setIsLoading(true);
    //   await apiClient.post('/auth/user/profile-image', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
    //   await queryClient.invalidateQueries({ queryKey: ['userSession'] });
    //   setSuccessMessage('Profile image updated successfully!');
    //   // User object in context would need to be updated with new profileImageUrl
    // } catch (err) { setError('Failed to upload image.'); } finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('yourProfile')}</h1>

          {/* Profile Picture Section - UI Only */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative w-32 h-32 mb-4">
              {imagePreview ? (
                <img src={imagePreview} alt="Profile" className="rounded-full w-full h-full object-cover" />
              ) : (
                <UserCircle className="w-full h-full text-gray-300" />
              )}
              <label htmlFor="profileImageInput" className="absolute bottom-0 right-0 bg-blue-600 p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors">
                <Camera className="w-4 h-4 text-white" />
                <input id="profileImageInput" type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>
            </div>
            {profileImage && ( // Show upload button only if a new image has been selected
                <Button onClick={handleImageUpload} disabled={isLoading} size="sm">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {t('uploadPicture')}
                </Button>
            )}
          </div>

          {/* User Details Section */}
          <div className="space-y-6">
            {/* Name Display/Edit */}
            <div>
              <Label htmlFor={isEditingName ? "editFullName" : ""} className="text-sm font-medium text-gray-500">{t('fullName')}</Label>
              {isEditingName ? (
                <form onSubmit={handleNameUpdateSubmit} className="mt-1 space-y-3">
                  <Input
                    id="editFullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full"
                    required
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <Button type="submit" disabled={isLoading || !fullName.trim()} size="sm">
                      {isLoading && !successMessage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {t('save')}
                    </Button>
                    <Button variant="outline" onClick={() => { setIsEditingName(false); setFullName(user?.name || ''); setError(null); setSuccessMessage(null); }} size="sm" type="button">
                      {t('cancel')}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-lg text-gray-900">{user.name || t('notSet')}</p>
                  <Button variant="link" onClick={() => { setIsEditingName(true); setSuccessMessage(null); setError(null); }} size="sm" className="text-blue-600 hover:text-blue-700">{t('edit')}</Button>
                </div>
              )}
            </div>

            {/* Email (Read-only) */}
            <div>
              <Label className="text-sm font-medium text-gray-500">{t('email')}</Label>
              <div className="mt-1 flex items-center text-gray-700">
                <Mail className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0" />
                <p className="text-lg truncate">{user.email}</p>
              </div>
            </div>

            {/* Phone (Read-only, example) */}
            <div>
              <Label className="text-sm font-medium text-gray-500">{t('phone')}</Label>
               <div className="mt-1 flex items-center text-gray-700">
                <Phone className="h-5 w-5 mr-2 text-gray-400 flex-shrink-0" />
                <p className="text-lg">{user.phone || t('notProvided')}</p> {/* Assuming user.phone exists */}
              </div>
            </div>

            {/* Messages */}
            {error && !isEditingName && ( // Only show general errors when not editing name, name errors are inline
              <div className="flex items-center text-sm text-red-600 bg-red-50 p-3 rounded-md">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {successMessage && (
              <div className="flex items-center text-sm text-green-600 bg-green-50 p-3 rounded-md">
                <span>{successMessage}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
